import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, Mail, Pencil, Power } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import ContactDialog from './onboarding/ContactDialog';
import type { FacilityPageViewer } from './utils/facilityPageTypes';
import {
    deriveFacilityStatus,
    type Facility,
    formatFacilityAddress,
    formatFacilityDisplayDate,
    isFacilityDeactivated,
} from './utils/utils';
import type { TFunction } from 'i18next';

interface FacilityDetailHeaderProps {
    facility: Facility;
    viewer: FacilityPageViewer;
    onBack: () => void;
    onEdit: () => void;
    onInviteAdmin: () => void;
    onDeactivate?: () => void;
}

export default function FacilityDetailHeader({
    facility,
    viewer,
    onBack,
    onEdit,
    onInviteAdmin,
    onDeactivate,
}: FacilityDetailHeaderProps) {
    const { t } = useTranslation();
    const [contactOpen, setContactOpen] = useState(false);
    const isNiAdmin = viewer === 'niAdmin';
    const facilityStatus = deriveFacilityStatus(facility);
    const trialCheckInUrl = process.env.REACT_APP_TRIAL_CHECKIN_URL;
    const canShowTrialCheckInAction =
        !isNiAdmin && facilityStatus === 'trial' && Boolean(trialCheckInUrl);

    const address = formatFacilityAddress(facility.address);
    const deactivated = isFacilityDeactivated(facility);
    const isOnboarding =
        facilityStatus === 'onboarding' || facilityStatus === 'not-started';
    const statusBadge = getStatusBadge(facility, isOnboarding, deactivated, t);

    return (
        <div className="mb-8 border-b border-white/10 pb-4">
            {isNiAdmin && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="mb-4 -ml-2"
                    onClick={onBack}
                >
                    <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('facilities.detail.backToFacilities')}
                </Button>
            )}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-3xl font-semibold text-foreground">
                        {facility.name}
                    </h1>
                    {(address || isNiAdmin) && (
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs text-muted-foreground">
                            {address && <span>{address}</span>}
                            {address && isNiAdmin && facility.region && (
                                <span className="text-muted-foreground/30">/</span>
                            )}
                            {isNiAdmin && facility.region && (
                                <span>{facility.region}</span>
                            )}
                            {isNiAdmin && facility.featureAccess && facility.featureAccess.length > 0 && (
                                <>
                                    <span className="text-muted-foreground/30">/</span>
                                    <span className="flex flex-wrap items-center gap-1">
                                        {facility.featureAccess.map((feature) => (
                                            <Badge
                                                key={feature}
                                                variant="outline"
                                                className="rounded-full border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium text-muted-foreground"
                                            >
                                                {feature}
                                            </Badge>
                                        ))}
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                    {statusBadge && (
                        <div className="mt-2">
                            <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                                    statusBadge.isActive
                                        ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
                                        : 'border-rose-500/20 bg-rose-500/10 text-rose-300'
                                }`}
                            >
                                {statusBadge.label}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {canShowTrialCheckInAction && (
                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-8 border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-200 hover:bg-emerald-500/20 hover:text-emerald-100"
                        >
                            <a
                                href={trialCheckInUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Calendar
                                    className="mr-1.5 h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                                {t('facilities.header.scheduleCheckIn')}
                            </a>
                        </Button>
                    )}
                    {isNiAdmin && onDeactivate && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-zinc-600/50 bg-zinc-700/40 text-xs text-muted-foreground hover:text-destructive"
                            onClick={onDeactivate}
                        >
                            <Power
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                            {t('facilities.header.deactivate')}
                        </Button>
                    )}
                    {isNiAdmin && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-zinc-600/50 bg-zinc-700/40 text-xs"
                            onClick={onInviteAdmin}
                        >
                            <Mail
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                            {t('facilities.header.inviteAdmin')}
                        </Button>
                    )}
                    {!isNiAdmin && isOnboarding && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-200 hover:bg-emerald-500/20 hover:text-emerald-100"
                            onClick={() => setContactOpen(true)}
                        >
                            <Mail
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                            {t('facilities.header.connectWithTeam')}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        className="h-8 text-xs"
                        onClick={onEdit}
                    >
                        <Pencil
                            className="mr-1.5 h-3.5 w-3.5"
                            aria-hidden="true"
                        />
                        {t('facilities.header.editFacility')}
                    </Button>
                </div>
            </div>
            <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
        </div>
    );
}

function getStatusBadge(
    facility: Facility,
    isOnboarding: boolean,
    deactivated: boolean,
    t: TFunction
): { label: string; isActive: boolean } | null {
    if (isOnboarding && deactivated) {
        return {
            label: t('facilities.header.onboardingCancelled', { date: formatFacilityDisplayDate(facility.end_date) }),
            isActive: false,
        };
    }
    if (isOnboarding) {
        return null;
    }
    if (!facility.isTrial) {
        let label: string;
        if (facility.end_date) {
            label = deactivated
                ? t('facilities.header.subscriptionEndedOn', { date: formatFacilityDisplayDate(facility.end_date) })
                : t('facilities.header.subscriptionActiveUntil', { date: formatFacilityDisplayDate(facility.end_date) });
        } else {
            label = deactivated
                ? t('facilities.header.subscriptionEnded')
                : t('facilities.header.subscriptionActive');
        }
        return { label, isActive: !deactivated };
    }
    return null;
}
